<template>
  <div
    class="tripadvisor-rating"
    :class="parentClass"
  >
    <div class="text-nowrap flex items-center justify-end">
      <span
        v-if="showTripAdvisorLogo"
        class="mr-1"
      >
        <img
          src="@packages/icons/tripadvisor-logo-round-green.png"
          alt="tripadvisor"
          width="20"
          height="20"
          loading="lazy"
        />
      </span>
      <span
        v-else-if="showSolresorLogo"
        class="mr-1"
      >
        <i-solresor-icon
          class="svg-icon"
          filled
          :font-controlled="false"
          alt="Solresor ikon"
          width="20"
          height="20"
          loading="lazy"
        />
      </span>
    </div>
    <template v-if="type === 'decimal'">
      <strong class="m-0 ml-1">
        {{ score }}
      </strong>
      <small class="m-0">
        /5
      </small>
      <span
        v-if="typeof reviewCount === 'number' && !hideCount"
        class="m-0 ml-1"
      >({{ reviewCount }})</span>
    </template>
    <svg
      v-if="type === 'dots'"
      height="10"
      :width="(5 * 10) * (score / 5)"
      color="currentColor"
      loading="lazy"
    >
      <circle
        v-for="(_, i) in 5"
        :key="i"
        :cx="5 + i * 10"
        cy="5"
        r="4"
        fill="currentColor"
      />
    </svg>
    <div
      v-if="showReviewCount"
      class="ml-2 -mb-1 text-slate-500 inline font-light"
      data-i18n="tripAdvisorOfReviews"
    >
      {{ $t('tripAdvisorOfReviews', parseInt(reviewCount)) }}
    </div>
  </div>
</template>

<script>
export default defineNuxtComponent({
  name: 'TripAdvisorRating',
  props: {
    rating: {
      type: Number,
      required: true,
    },

    hideCount: {
      type: Boolean,
      default: true,
    },

    reviewCount: {
      type: Number,
      default: null,
    },

    showTripAdvisorLogo: {
      type: Boolean,
      default: true,
    },

    showSolresorLogo: {
      type: Boolean,
      default: true,
    },

    showReviewCount: {
      type: Boolean,
      default: false,
    },

    parentClass: {
      type: String,
      default: 'flex',
    },

    /**
     * Ignored when type=dots
     */
    size: {
      type: String,
      default: 'small',
      validator: value => ['xs', 'small', 'medium', 'large'].includes(value),
    },

    type: {
      type: String,
      default: 'decimal',
      validator: value => ['decimal', 'dots'].includes(value),
    },
  },

  computed: {
    score () {
      return Number.parseFloat(this.rating).toFixed(2)
    },
  },

  methods: {
    getClasses (index) {
      const classes = ['hidden']

      index <= this.rating && classes.push('tripadvisor-star-full')
      index - 0.5 === roundNumberToStep(this.rating, 0.5) && classes.push('tripadvisor-star-half')
      classes.push(this.size)

      return classes
    },
  },
})
</script>

<style lang="scss" scoped>
.tripadvisor {
  &-rating {
    strong, svg {
      color: theme('colors.black');
    }
    span {
      text-transform: lowercase;
      color: theme('colors.black');
      font-weight: normal;
    }
    small {
      margin-left: 2px !important;
      font-size: .65em;
      color: theme('colors.black');
      position: relative;
      top: -2px;
    }
  }

  &-star {
    display: inline-block;

    width: 12px;
    height: 12px;
    margin-left: 1px;
    margin-right: 0 !important;

    border: 2px solid theme('colors.tripadvisor-green');
    border-radius: 50px;

    &.medium {
      width: 16px;
      height: 16px;
    }

    &.large {
      width: 24px;
      height: 24px;
    }

    &.xs {
      width: 10px;
      height: 10px;
    }

    &-full {
      background: theme('colors.tripadvisor-green');
    }

    &-half {
      background: theme('colors.tripadvisor-green');
      position: relative;

      &:after {
        content: "";
        position: absolute;
        background: white;
        top: 0px;
        bottom: 0px;
        right: 0px;
        width: 50%;
        border-radius: 0 90px 90px 0;
      }
    }
  }
}
.reference-t-2 small{
  top: 2px;
}
</style>
